// components/ModalPinEmpresa.jsx
// Modal compartido para verificar PIN antes de cambiar de empresa contable.
// Usado en EmpresasContables.jsx y Contabilidad.jsx.
import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';

const BLOQUEO_KEY = 'pin_bloqueo_hasta';

const inputSty    = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const labelSty    = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#374151' };
const pinInputSty = { ...inputSty, fontSize: 24, letterSpacing: 12, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 };
const btnPrimSty  = { width: '100%', padding: '10px 0', background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 8 };
const btnSecSty   = { width: '100%', padding: '8px 0', background: 'none', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', marginTop: 6 };
const errSty      = { color: '#dc2626', fontSize: 12, marginTop: 8, textAlign: 'center', minHeight: 18 };
const ovSty       = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const modSty      = { background: '#fff', borderRadius: 14, padding: 32, width: 360, boxShadow: '0 8px 32px rgba(0,0,0,.18)' };

export default function ModalPinEmpresa({ empresa, onExito, onCancelar }) {
  const [fase, setFase]           = useState('pin'); // 'pin' | 'password' | 'nuevo_pin'
  const [pin, setPin]             = useState('');
  const [password, setPassword]   = useState('');
  const [nuevoPin, setNuevoPin]   = useState('');
  const [nuevoPin2, setNuevoPin2] = useState('');
  const [intentos, setIntentos]   = useState(0);
  const [error, setError]         = useState('');
  const [cargando, setCargando]   = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, [fase]);

  const bloqueadoHasta = () => {
    const ts = localStorage.getItem(BLOQUEO_KEY);
    if (!ts) return null;
    const hasta = parseInt(ts, 10);
    return Date.now() < hasta ? hasta : null;
  };

  const minutosBloqueo = () => {
    const hasta = bloqueadoHasta();
    if (!hasta) return 0;
    return Math.ceil((hasta - Date.now()) / 60000);
  };

  const handleVerificarPin = async () => {
    if (!/^\d{4}$/.test(pin)) { setError('El PIN debe ser exactamente 4 dígitos.'); return; }
    setCargando(true); setError('');
    try {
      await api.post(`/empresas-contables/${empresa.id}/verificar-pin`, { pin });
      onExito(empresa);
    } catch (err) {
      const nuevosIntentos = intentos + 1;
      setIntentos(nuevosIntentos);
      setPin('');
      setError(nuevosIntentos >= 3
        ? 'PIN incorrecto. 3 intentos fallidos.'
        : `PIN incorrecto. Intento ${nuevosIntentos}/3.`);
    } finally { setCargando(false); }
  };

  const handleVerificarPassword = async () => {
    if (!password) { setError('Ingresa tu contraseña.'); return; }
    if (bloqueadoHasta()) { setError(`Bloqueado por ${minutosBloqueo()} min más.`); return; }
    setCargando(true); setError('');
    try {
      await api.post('/empresas-contables/verificar-password', { password });
      setFase('nuevo_pin');
      setPassword(''); setError('');
    } catch (err) {
      setPassword('');
      if (err.response?.status === 400) {
        localStorage.setItem(BLOQUEO_KEY, String(Date.now() + 5 * 60 * 1000));
        setError('Contraseña incorrecta. Bloqueado 5 minutos por seguridad.');
      } else {
        setError('No se pudo verificar la contraseña.');
      }
    } finally { setCargando(false); }
  };

  const handleConfigurarPin = async () => {
    if (!/^\d{4}$/.test(nuevoPin)) { setError('El PIN debe ser 4 dígitos numéricos.'); return; }
    if (nuevoPin !== nuevoPin2)    { setError('Los PINs no coinciden.'); return; }
    setCargando(true); setError('');
    try {
      await api.post(`/empresas-contables/${empresa.id}/configurar-pin`, { pin: nuevoPin });
      localStorage.removeItem(BLOQUEO_KEY);
      onExito(empresa);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar el PIN.');
    } finally { setCargando(false); }
  };

  return (
    <div style={ovSty} onClick={e => { if (e.target === e.currentTarget) onCancelar(); }}>
      <div style={modSty}>

        {/* ── Fase PIN ── */}
        {fase === 'pin' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔐</div>
              <h3 style={{ margin: 0, fontSize: 17, color: '#1e3a5f' }}>PIN de acceso</h3>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}><strong>{empresa.nombre}</strong></p>
            </div>
            <label style={labelSty}>Ingresa el PIN de 4 dígitos</label>
            <input ref={inputRef} style={pinInputSty} type="password" inputMode="numeric"
              maxLength={4} placeholder="••••" value={pin}
              onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              onKeyDown={e => e.key === 'Enter' && handleVerificarPin()} />
            <p style={errSty}>{error}</p>
            <button style={btnPrimSty} onClick={handleVerificarPin} disabled={cargando || pin.length !== 4}>
              {cargando ? 'Verificando...' : 'Confirmar'}
            </button>
            {intentos >= 3 && (
              <button style={{ ...btnSecSty, color: '#b45309', borderColor: '#fcd34d', marginTop: 10 }}
                onClick={() => { setFase('password'); setError(''); setPin(''); }}>
                ¿Olvidaste el PIN? Usar contraseña
              </button>
            )}
            <button style={btnSecSty} onClick={onCancelar}>Cancelar</button>
          </>
        )}

        {/* ── Fase contraseña admin ── */}
        {fase === 'password' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔑</div>
              <h3 style={{ margin: 0, fontSize: 17, color: '#92400e' }}>Verificación de administrador</h3>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
                Ingresa tu contraseña de administrador para acceder a <strong>{empresa.nombre}</strong> y configurar un nuevo PIN.
              </p>
            </div>
            {bloqueadoHasta() ? (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', textAlign: 'center' }}>
                <p style={{ margin: 0, color: '#dc2626', fontWeight: 600, fontSize: 14 }}>
                  🔒 Bloqueado por {minutosBloqueo()} minuto{minutosBloqueo() !== 1 ? 's' : ''} más.
                </p>
                <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: 12 }}>Contraseña incorrecta ingresada previamente.</p>
              </div>
            ) : (
              <>
                <label style={labelSty}>Contraseña de administrador</label>
                <input ref={inputRef} style={inputSty} type="password" placeholder="Tu contraseña"
                  value={password} onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleVerificarPassword()} />
              </>
            )}
            <p style={errSty}>{error}</p>
            {!bloqueadoHasta() && (
              <button style={{ ...btnPrimSty, background: '#92400e' }} onClick={handleVerificarPassword} disabled={cargando || !password}>
                {cargando ? 'Verificando...' : 'Verificar contraseña'}
              </button>
            )}
            <button style={btnSecSty} onClick={() => { setFase('pin'); setError(''); setPassword(''); }}>← Volver al PIN</button>
            <button style={btnSecSty} onClick={onCancelar}>Cancelar</button>
          </>
        )}

        {/* ── Fase nuevo PIN ── */}
        {fase === 'nuevo_pin' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🆕</div>
              <h3 style={{ margin: 0, fontSize: 17, color: '#059669' }}>Configurar nuevo PIN</h3>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
                Define un nuevo PIN de 4 dígitos para <strong>{empresa.nombre}</strong>.
              </p>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelSty}>Nuevo PIN (4 dígitos)</label>
              <input ref={inputRef} style={pinInputSty} type="password" inputMode="numeric"
                maxLength={4} placeholder="••••" value={nuevoPin}
                onChange={e => setNuevoPin(e.target.value.replace(/\D/g, '').slice(0, 4))} />
            </div>
            <div style={{ marginBottom: 4 }}>
              <label style={labelSty}>Confirmar nuevo PIN</label>
              <input style={pinInputSty} type="password" inputMode="numeric"
                maxLength={4} placeholder="••••" value={nuevoPin2}
                onChange={e => setNuevoPin2(e.target.value.replace(/\D/g, '').slice(0, 4))}
                onKeyDown={e => e.key === 'Enter' && handleConfigurarPin()} />
            </div>
            <p style={errSty}>{error}</p>
            <button style={{ ...btnPrimSty, background: '#059669' }} onClick={handleConfigurarPin}
              disabled={cargando || nuevoPin.length !== 4 || nuevoPin2.length !== 4}>
              {cargando ? 'Guardando...' : '✓ Guardar PIN y acceder'}
            </button>
            <button style={btnSecSty} onClick={onCancelar}>Cancelar</button>
          </>
        )}

      </div>
    </div>
  );
}
